import React from "react";

import { Box, Button, TextField, Theme, Typography } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { MuiOtpInput } from "mui-one-time-password-input";
import { Controller, useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import PasswordTextField from "@/components/PasswordTextField";
import { colors } from "@/theme/overrides/colors";

import BrandPanel from "./components/BrandPanel";
import ResendButton from "./components/ResendButton";
import { FormValues } from "./LoginPage";
import nutricoachLogo from "../../../assets/Nutricoach.png";

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: "flex",
    minHeight: "100vh",
    width: "100%",
    backgroundColor: colors.white,
  },
  loader: {
    position: "fixed",
    top: 0,
    right: 0,
    left: 0,
    zIndex: 2,
  },
  formPane: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    backgroundColor: colors.white,
    WebkitFontSmoothing: "antialiased",
    [theme.breakpoints.up("md")]: {
      padding: "48px 40px",
    },
  },
  formInner: {
    width: "100%",
    maxWidth: "396px",
    display: "flex",
    flexDirection: "column",
    // Stagger the form in so it doesn't pop as one block.
    "& > *": {
      animation: "$fadeUp 500ms cubic-bezier(0.2, 0, 0, 1) both",
    },
    "& > *:nth-of-type(2)": { animationDelay: "70ms" },
    "& > *:nth-of-type(3)": { animationDelay: "140ms" },
  },
  logo: {
    width: "184px",
    objectFit: "contain",
    marginBottom: "40px",
  },
  heading: {
    fontWeight: 800,
    letterSpacing: "-0.6px",
    textWrap: "balance",
    color: colors.mapped.text.heading,
  },
  subheading: {
    marginTop: "6px",
    color: colors.mapped.text.body,
  },
  fields: {
    marginTop: "28px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  metaRow: {
    marginTop: "2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  link: {
    textDecoration: "none",
    color: colors.theme.primary.dark,
    fontWeight: 700,
  },
  forgotLink: {
    textDecoration: "none",
    color: colors.mapped.text.body,
    fontWeight: 600,
  },
  submit: {
    marginTop: "24px",
    transition: "transform 120ms cubic-bezier(0.2, 0, 0, 1)",
    "&:active": {
      transform: "scale(0.96)",
    },
  },
  otpWrap: {
    marginTop: "28px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
  },
  muiOtp: {
    width: "100%",
    // Keep digits from shifting as the user types.
    "& input": {
      fontVariantNumeric: "tabular-nums",
    },
    // Hide number-input spinners.
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
      display: "none",
    },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
  },
  secureNote: {
    marginTop: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    color: colors.mapped.text.placeholder,
  },
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
}));

type Props = {
  isLookUpSuccess: boolean;
  onResend: VoidFunction;
};

const LoginForm: React.FC<Props> = ({ isLookUpSuccess, onResend }) => {
  const { classes } = useStyles();
  const {
    control,
    register,
    formState: { errors, isSubmitting },
  } = useFormContext<FormValues>();

  return (
    <Box className={classes.root}>
      {isSubmitting && <LinearProgress className={classes.loader} />}

      <BrandPanel />

      <Box className={classes.formPane}>
        <Box className={classes.formInner}>
          <img src={nutricoachLogo} alt="NutriCoach" className={classes.logo} />

          {isLookUpSuccess ? (
            <>
              <Typography variant="h4" className={classes.heading}>
                {"Verify it's you"}
              </Typography>
              <Typography variant="body1" className={classes.subheading}>
                We sent a 6-digit code to your email. Enter it below to finish
                signing in.
              </Typography>

              <Box className={classes.otpWrap}>
                <Controller
                  control={control}
                  name="otp_attempt"
                  render={({ field: { onChange, value } }) => (
                    <MuiOtpInput
                      className={classes.muiOtp}
                      value={value}
                      onChange={onChange}
                      length={6}
                      TextFieldsProps={{
                        error: !!errors.otp_attempt,
                        helperText: errors.otp_attempt?.message ?? " ",
                        type: "number",
                        required: true,
                        placeholder: "-",
                      }}
                    />
                  )}
                />
                <ResendButton onResend={onResend} delay={30} />
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h4" className={classes.heading}>
                Welcome back
              </Typography>
              <Typography variant="body1" className={classes.subheading}>
                Log in to your dashboard to keep coaching.
              </Typography>

              <Box className={classes.fields}>
                <TextField
                  {...register("email")}
                  error={!!errors.email}
                  helperText={errors.email?.message ?? " "}
                  label="Email"
                  type="email"
                  autoComplete="email"
                  required
                  fullWidth
                />
                <PasswordTextField
                  {...register("password")}
                  error={!!errors.password}
                  helperText={errors.password?.message ?? " "}
                  label="Password"
                  autoComplete="current-password"
                  required
                  fullWidth
                />
              </Box>

              <Box className={classes.metaRow}>
                <Typography variant="caption" color="textSecondary">
                  {"Don't have an account? "}
                  <Link to="/register" className={classes.link}>
                    Register
                  </Link>
                </Typography>
                <Typography variant="caption">
                  <Link to="/forgot-password" className={classes.forgotLink}>
                    Forgot password?
                  </Link>
                </Typography>
              </Box>
            </>
          )}

          <Button
            className={classes.submit}
            disabled={isSubmitting}
            size="large"
            type="submit"
            fullWidth
          >
            {isLookUpSuccess ? "Verify & sign in" : "Log in"}
          </Button>

          <Typography variant="caption" className={classes.secureNote}>
            Secured with two-factor authentication
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginForm;
